import {
  IonContent,
  IonIcon,
  IonPage,
  IonRouterLink,
  IonSpinner,
} from "@ionic/react";
import { useEffect, useState } from "react";
import { isNativeiOS } from "../../lib/utils/platform";
import { isEmbroideryTrace } from "../../config/appVariant";

import googleicon from "../../assets/google.svg";

import { logoApple } from "ionicons/icons";
import { signInWithApple, signInWithGoogle } from "../../lib/firebase/firebase";

import { useHistory } from "react-router";
import { useAuth } from "../../contexts/AuthContext";
import { handleSignUp, createOAuthUserDocument } from "../../lib/firebase/firebase";
import { getUTMForFirebase, getUTMForAnalytics } from "../../lib/analytics/utmTracking";
import { trackSignUp } from "../../lib/analytics/metaPixel";
import ReactGA from "react-ga4";

import { faCrown } from "@fortawesome/pro-solid-svg-icons";
import { faEnvelope, faLockKeyhole, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import logo from "../../assets/logo1.svg";
import branding from "../../config/branding";
import "./SignIn.css";

const SignUp = () => {
  const history = useHistory();
  const { user, userIsPremium, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const getReadableError = (errorCode) => {
    switch (errorCode) {
      case "auth/email-already-exists":
      case "auth/email-already-in-use":
        return "The email you have entered is already in use. Please use a different email.";
      case "auth/invalid-email":
        return "The email you have entered is not valid. Please check your email and try again.";
      case "auth/invalid-password":
        return "The password you have entered is not valid. It must be at least six characters long.";
      case "auth/user-not-found":
        return "The entered credentials do not match any account. Please try again.";
      case "auth/phone-number-already-exists":
        return "The phone number you have entered is already in use. Please use a different phone number.";
      case "auth/invalid-uid":
        return "The user ID is not valid. Please try again.";
      case "auth/weak-password":
        return "The password you have entered is too weak. Please enter a stronger password.";
      default:
        return errorCode;
    }
  };

  function validatePassword(password) {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
    return regex.test(password);
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      onSubmit();
    }
  };

  const onSubmit = async () => {
    if (!validatePassword(password)) {
      setError(
        "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character out of !@#$%^&*"
      );
      return;
    }

    if (!email || !password || !passwordCheck) {
      setError("Please fill in all fields");
      return;
    }
    if (password !== passwordCheck) {
      setError("Passwords do not match");
      return;
    }
    try {
      setLoading(true);
      const utmData = getUTMForFirebase();
      await handleSignUp(email, password, utmData);
      
      const utmAnalytics = getUTMForAnalytics();
      ReactGA.event('sign_up', {
        method: 'email',
        utm_source: utmAnalytics.source,
        utm_medium: utmAnalytics.medium,
        utm_campaign: utmAnalytics.campaign,
        utm_term: utmAnalytics.term,
        utm_content: utmAnalytics.content,
      });
      trackSignUp('email');
    } catch (error) {
      console.error("Sign-up error:", error);
      setError(getReadableError(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await signInWithGoogle();
      
      if (!result) {
        return;
      }
      
      const user = result.user;
      if (result.additionalUserInfo?.isNewUser) {
        console.log("New user signed up with Google:", user);
        
        const utmData = getUTMForFirebase();
        await createOAuthUserDocument(user.uid, user.email || '', utmData);
        
        const utmAnalytics = getUTMForAnalytics();
        ReactGA.event('sign_up', {
          method: 'google',
          utm_source: utmAnalytics.source,
          utm_medium: utmAnalytics.medium,
          utm_campaign: utmAnalytics.campaign,
          utm_term: utmAnalytics.term,
          utm_content: utmAnalytics.content,
        });
        trackSignUp('google');
      } else {
        console.log("Existing user signed in with Google:", user);
      }
    } catch (error) {
      console.error("Error during Google sign-up:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-up was cancelled.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        return;
      } else {
        setError("Failed to sign up with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await signInWithApple();
      
      if (!result) {
        return;
      }
      
      const user = result.user;
      if (result.additionalUserInfo?.isNewUser) {
        console.log("New user signed up with Apple:", user);
        
        const utmData = getUTMForFirebase();
        await createOAuthUserDocument(user.uid, user.email || '', utmData);
        
        const utmAnalytics = getUTMForAnalytics();
        ReactGA.event('sign_up', {
          method: 'apple',
          utm_source: utmAnalytics.source,
          utm_medium: utmAnalytics.medium,
          utm_campaign: utmAnalytics.campaign,
          utm_term: utmAnalytics.term,
          utm_content: utmAnalytics.content,
        });
        trackSignUp('apple');
      } else {
        console.log("Existing user signed in with Apple:", user);
      }
    } catch (error) {
      console.error("Error during Apple sign-up:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-up was cancelled.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        return;
      } else {
        setError("Failed to sign up with Apple. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    
    const oauthRedirectComplete = sessionStorage.getItem('oauthRedirectComplete');
    if (oauthRedirectComplete) {
      sessionStorage.removeItem('oauthRedirectComplete');
      console.log("🔐 SignUp: OAuth redirect detected, redirecting to /home immediately");
      history.push("/home");
      return;
    }
    
    if (authLoading) return;
    
    if (userIsPremium || isNativeiOS() || isEmbroideryTrace) {
      history.push("/home");
    } else {
      history.push("/checkout");
    }
  }, [user, userIsPremium, authLoading, history]);

  return (
    <IonPage>
      <IonContent className="auth-background">
        <div className="auth-wrapper">
          <div className="auth-card">
            <div className="auth-logo">
              {isEmbroideryTrace ? (
                <img src={branding.logo} alt={branding.appName} style={{ height: "44px", width: "auto", objectFit: "contain" }} />
              ) : (
                <>
                  <img src={logo} alt="SvgTrace" />
                  <span className="pro-badge">
                    <FontAwesomeIcon icon={faCrown} /> PRO
                  </span>
                </>
              )}
            </div>

            <div className="auth-heading">
              <h2>Create your account</h2>
              <p>Start turning images into stitch-ready files — free</p>
            </div>

            {error && (
              <div className="auth-error">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {error}
              </div>
            )}

            <form className="auth-form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
              <div className="auth-input-group">
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
              </div>

              <div className="auth-input-group">
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <FontAwesomeIcon icon={faLockKeyhole} className="input-icon" />
              </div>

              <div className="auth-input-group">
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={passwordCheck}
                  onChange={(e) => setPasswordCheck(e.target.value)}
                  onKeyDown={handleKeyDown}
                  required
                />
                <FontAwesomeIcon icon={faLockKeyhole} className="input-icon" />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? <IonSpinner name="crescent" /> : "Create account"}
              </button>

              <div className="auth-divider">
                <span>or</span>
              </div>

              <div className="auth-social-buttons">
                <button
                  type="button"
                  className="auth-social-btn"
                  onClick={handleGoogleSignUp}
                >
                  <img src={googleicon} alt="Google" />
                  Google
                </button>
                <button
                  type="button"
                  className="auth-social-btn"
                  onClick={handleAppleSignIn}
                >
                  <IonIcon icon={logoApple} />
                  Apple
                </button>
              </div>
            </form>

            <p className="auth-prompt">
              Already have an account?{" "}
              <IonRouterLink routerLink="/signin">Sign In</IonRouterLink>
            </p>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SignUp;
